import { FeedbackPoint, HapticPreset, TrackAnalysis } from "./types";

export class AudioAnalyzer {
  private audioWorkletSupported: boolean = false;
  private workletPath: string = "/analyzers/frequency-analyzer-processor.js";

  constructor() {
    // Check if AudioWorklet is supported
    this.audioWorkletSupported = typeof AudioWorkletNode === "function";
  }

  public async analyzeTrack(audioBuffer: AudioBuffer, preset: HapticPreset): Promise<TrackAnalysis> {
    // Existing overall energy calculation can remain if useful
    const overallEnergy = this.calculateEnergy(audioBuffer);
    // Call the new calculateFeedbackPoints, which no longer needs overallEnergy passed in
    const feedbackPoints = await this.calculateFeedbackPoints(audioBuffer, preset);

    return {
      trackId: "", // Should be set by caller
      energy: overallEnergy, // Keep overall energy if it's still meaningful
      feedbackPoints,
      timestamp: Date.now(),
    };
  }

  private calculateEnergy(audioBuffer: AudioBuffer): number {
    const data = audioBuffer.getChannelData(0); // Assuming mono
    let energy = 0;

    for (let i = 0; i < data.length; i++) {
      energy += data[i] * data[i];
    }

    return energy / data.length;
  }
  private async calculateFeedbackPoints(audioBuffer: AudioBuffer, preset: HapticPreset): Promise<FeedbackPoint[]> {
    const points: FeedbackPoint[] = [];
    const sampleRate = audioBuffer.sampleRate; // Use audioBuffer's sampleRate for offline context

    // OfflineAudioContext for processing the entire buffer
    const offlineContext = new OfflineAudioContext(audioBuffer.numberOfChannels, audioBuffer.length, sampleRate);

    // Local AnalyserNode for this offline analysis
    const offlineAnalyzer = offlineContext.createAnalyser();
    offlineAnalyzer.fftSize = preset.fftSize ?? 2048; // Use preset or default
    const frequencyBinCount = offlineAnalyzer.frequencyBinCount;
    const frequencyData = new Float32Array(frequencyBinCount);

    // Define parameters for onset detection, using preset values or defaults
    const hopSize = preset.hopSize ?? 512; // Use preset or default
    const currentTargetFrequencyBand = preset.targetFrequencyBand ?? { min: 60, max: 120 }; // Use preset or default
    let previousSubBandEnergy = 0;

    // AudioBufferSourceNode
    const source = offlineContext.createBufferSource();
    source.buffer = audioBuffer;

    // Use AudioWorklet if supported, fallback to ScriptProcessor if not
    if (this.audioWorkletSupported) {
      // Load the audio worklet module
      try {
        await offlineContext.audioWorklet.addModule(this.workletPath);

        // Create an AudioWorkletNode
        const audioWorkletNode = new AudioWorkletNode(offlineContext, "frequency-analyzer-processor");

        // Initialize the processor with the hop size
        audioWorkletNode.port.postMessage({
          type: "init",
          bufferSize: hopSize,
        });

        // Set up message handling from the AudioWorklet
        audioWorkletNode.port.onmessage = (event) => {
          if (event.data.type === "audioData") {
            // Get frequency data for the current chunk
            offlineAnalyzer.getFloatFrequencyData(frequencyData);

            const currentSubBandEnergy = this.calculateSubBandEnergy(
              frequencyData,
              sampleRate,
              frequencyBinCount,
              currentTargetFrequencyBand
            );

            // Onset detection logic
            const onsetSensitivityFactor = preset.onsetSensitivity ?? 0.5; // Default if not set
            const minimumAbsoluteEnergy = 0.01; // Avoid detecting onsets in silence

            if (
              currentSubBandEnergy > previousSubBandEnergy * (1 + onsetSensitivityFactor) &&
              currentSubBandEnergy > minimumAbsoluteEnergy
            ) {
              // Calculate timestamp based on the current frame index and sample rate
              const timestamp = event.data.currentFrame / sampleRate;
              points.push({
                timestamp: timestamp,
                intensity: preset.intensity * (currentSubBandEnergy / (previousSubBandEnergy + 0.001)), // Relative intensity
                frequency: (currentTargetFrequencyBand.min + currentTargetFrequencyBand.max) / 2, // Representative frequency from the band
              });
            }
            previousSubBandEnergy = currentSubBandEnergy;
          }
        };

        // Connect the graph: source -> analyzer -> audioWorkletNode -> destination
        source.connect(offlineAnalyzer);
        offlineAnalyzer.connect(audioWorkletNode);
        audioWorkletNode.connect(offlineContext.destination);
      } catch (error) {
        console.warn("AudioWorklet failed to load, falling back to ScriptProcessor:", error);
        // Fall back to ScriptProcessor if AudioWorklet fails
        this.audioWorkletSupported = false;
        // Re-call this method to use the fallback
        return this.calculateFeedbackPoints(audioBuffer, preset);
      }
    } else {
      // Fallback to ScriptProcessorNode (deprecated but still works)
      console.warn("AudioWorklet not supported, using deprecated ScriptProcessorNode");
      const scriptNode = offlineContext.createScriptProcessor(
        hopSize,
        audioBuffer.numberOfChannels,
        audioBuffer.numberOfChannels
      );

      scriptNode.onaudioprocess = (audioProcessingEvent) => {
        // Get frequency data for the current chunk
        offlineAnalyzer.getFloatFrequencyData(frequencyData);

        const currentSubBandEnergy = this.calculateSubBandEnergy(
          frequencyData,
          sampleRate,
          frequencyBinCount,
          currentTargetFrequencyBand // Use potentially preset-defined band
        );

        // Onset detection logic
        const onsetSensitivityFactor = preset.onsetSensitivity ?? 0.5; // Default if not set
        const minimumAbsoluteEnergy = 0.01; // Avoid detecting onsets in silence

        if (
          currentSubBandEnergy > previousSubBandEnergy * (1 + onsetSensitivityFactor) &&
          currentSubBandEnergy > minimumAbsoluteEnergy
        ) {
          points.push({
            timestamp: audioProcessingEvent.playbackTime, // Timestamp from the audio processing event
            intensity: preset.intensity * (currentSubBandEnergy / (previousSubBandEnergy + 0.001)), // Relative intensity
            frequency: (currentTargetFrequencyBand.min + currentTargetFrequencyBand.max) / 2, // Representative frequency from the band
          });
        }
        previousSubBandEnergy = currentSubBandEnergy;
      };

      // Connect the graph: source -> analyzer -> scriptNode -> destination
      source.connect(offlineAnalyzer);
      offlineAnalyzer.connect(scriptNode);
      scriptNode.connect(offlineContext.destination); // Necessary for ScriptProcessorNode to run
    }

    // Start processing
    source.start(0);
    await offlineContext.startRendering(); // This promise resolves when all processing is complete

    return points;
  }

  // Helper method to calculate energy in a specific frequency sub-band
  private calculateSubBandEnergy(
    frequencyData: Float32Array,
    sampleRate: number,
    frequencyBinCount: number,
    band: { min: number; max: number }
  ): number {
    let energy = 0;
    // Calculate the range of FFT bins that correspond to the frequency band
    const nyquist = sampleRate / 2;
    const minIndex = Math.max(0, Math.floor((band.min / nyquist) * frequencyBinCount));
    const maxIndex = Math.min(frequencyBinCount - 1, Math.ceil((band.max / nyquist) * frequencyBinCount));

    if (minIndex > maxIndex) {
      return 0; // Invalid band or very narrow band
    }

    for (let j = minIndex; j <= maxIndex; j++) {
      // frequencyData[j] is in dBFS. Convert to linear scale (power).
      // Power = 10^(dB/10). We use 20 for amplitude, but for power-like calculations from dB, 10 is standard.
      // Let's assume the values are magnitudes, so square them for energy, or if they are dB, convert properly.
      // AnalyserNode.getFloatFrequencyData() returns dBFS.
      energy += Math.pow(10, frequencyData[j] / 10);
    }

    const numBinsInBand = maxIndex - minIndex + 1;
    return numBinsInBand > 0 ? energy / numBinsInBand : 0; // Average energy in the band
  }
}
